'use strict';

/**
 * Python 3.11 string and number semantics the backend validates open answers
 * with, so a front-end rule never rejects what the backend accepts (RPI-1136).
 * Each helper names the Python it mirrors. Pure: no globals, no DOM.
 */

// `str.isspace()`, which is also what `\s` matches in a `str` pattern. Not
// JavaScript's `\s`: that adds U+FEFF and lacks U+001C-U+001F and U+0085.
var SPACE =
  '\\t\\n\\u000b\\f\\r\\u001c-\\u001f \\u0085\\u00a0\\u1680' +
  '\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';
var SPACE_RUNS = new RegExp('[' + SPACE + ']+', 'g');
var EDGE_SPACE = new RegExp('^[' + SPACE + ']+|[' + SPACE + ']+$', 'g');

/** `str.strip()`. */
export function strip(text) {
  return text.replace(EDGE_SPACE, '');
}

/** gryphon `util.simplify_whitespace`: every run becomes one space, then strip. */
export function simplifyWhitespace(text) {
  return strip(text.replace(SPACE_RUNS, ' '));
}

/** `len(str)`: code points, not UTF-16 units. */
export function pyLength(text) {
  return Array.from(text).length;
}

var DECIMAL_DIGIT = /\p{Nd}/u;
var IS_SPACE = new RegExp('[' + SPACE + ']');

/**
 * The ASCII digit for a Unicode decimal digit (`unicodedata.decimal`), or
 * null. Unicode encodes every decimal digit in a run of ten starting at zero,
 * so a digit's value is its distance from the start of its run.
 */
function asciiDigit(ch) {
  if (!DECIMAL_DIGIT.test(ch)) {
    return null;
  }
  var code = ch.codePointAt(0);
  var before = 0;
  while (DECIMAL_DIGIT.test(String.fromCodePoint(code - before - 1))) {
    before++;
  }
  return String(before % 10);
}

/**
 * CPython's conversion of a numeric string to ASCII before parsing: ASCII
 * stays, other whitespace becomes a space, other decimal digits become ASCII
 * digits, and anything else makes the string invalid (null). `Decimal` also
 * drops every underscore here (`ignoreUnderscores`); `int` checks them later.
 */
function toAscii(text, ignoreUnderscores) {
  var out = '';
  var chars = Array.from(text);
  for (var i = 0; i < chars.length; i++) {
    var ch = chars[i];
    var code = ch.codePointAt(0);
    if (ignoreUnderscores && ch === '_') {
      continue;
    }
    if (code > 0 && code <= 127) {
      out += ch;
    } else if (IS_SPACE.test(ch)) {
      out += ' ';
    } else {
      var digit = asciiDigit(ch);
      if (digit === null) {
        return null;
      }
      out += digit;
    }
  }
  return out;
}

function finite(negative, digits, exp) {
  return {
    special: null,
    negative: negative,
    coeff: digits.replace(/^0+(?=\d)/, ''),
    exp: exp,
  };
}

// Python 3.11's default `sys.int_info.str_digits_check_threshold` limit.
var INT_MAX_STR_DIGITS = 4300;

/**
 * `int(text)` in base 10: a number (see `parsePyDecimal` for the shape, with
 * `exp` 0), or null where Python raises `ValueError`.
 */
export function parsePyInt(text) {
  var ascii = toAscii(text, false);
  var match =
    ascii === null
      ? null
      : /^[ \t\n\v\f\r]*([+-]?)(\d(?:_?\d)*)[ \t\n\v\f\r]*$/.exec(ascii);
  if (!match) {
    return null;
  }
  var digits = match[2].replace(/_/g, '');
  if (digits.length > INT_MAX_STR_DIGITS) {
    return null;
  }
  return finite(match[1] === '-', digits, 0);
}

/**
 * `Decimal(text)`, or null where Python raises `InvalidOperation`. A finite
 * value is `{ special: null, negative, coeff, exp }`, worth `coeff * 10**exp`
 * with `coeff` a digit string without leading zeros. `special` is `'inf'` or
 * `'nan'` (sNaN too), or `'unknown'` for an exponent too large to mirror
 * exactly (15 digits or more), which a caller must let through.
 */
export function parsePyDecimal(text) {
  var ascii = toAscii(strip(text), true);
  if (ascii === null) {
    return null;
  }
  var match = /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:e([+-]?)(\d+))?$/i.exec(
    ascii
  );
  if (match) {
    var whole = match[2] !== undefined ? match[2] : '';
    var fraction = match[2] !== undefined ? match[3] || '' : match[4];
    var expDigits = (match[6] || '0').replace(/^0+(?=\d)/, '');
    if (expDigits.length >= 15) {
      return { special: 'unknown', negative: match[1] === '-' };
    }
    var exp = Number(expDigits) * (match[5] === '-' ? -1 : 1);
    return finite(match[1] === '-', whole + fraction, exp - fraction.length);
  }
  var special = /^([+-]?)(?:(inf|infinity)|s?nan\d*)$/i.exec(ascii);
  if (!special) {
    return null;
  }
  return {
    special: special[2] ? 'inf' : 'nan',
    negative: special[1] === '-',
  };
}

function signum(value) {
  if (value.special === null && /^0+$/.test(value.coeff)) {
    return 0;
  }
  return value.negative ? -1 : 1;
}

/**
 * Python's ordering of two `parsePyDecimal`/`parsePyInt` values that are finite
 * or infinite (a NaN raises in Python; callers must not pass one): -1, 0, 1.
 */
export function compareNumbers(a, b) {
  var sa = signum(a);
  var sb = signum(b);
  if (sa !== sb) {
    return sa > sb ? 1 : -1;
  }
  if (sa === 0) {
    return 0;
  }
  if (a.special === 'inf' || b.special === 'inf') {
    if (a.special === b.special) {
      return 0;
    }
    return a.special === 'inf' ? sa : -sa;
  }
  var adjustedA = a.exp + a.coeff.length - 1;
  var adjustedB = b.exp + b.coeff.length - 1;
  if (adjustedA !== adjustedB) {
    return adjustedA > adjustedB ? sa : -sa;
  }
  var width = Math.max(a.coeff.length, b.coeff.length);
  var digitsA = a.coeff.padEnd(width, '0');
  var digitsB = b.coeff.padEnd(width, '0');
  if (digitsA === digitsB) {
    return 0;
  }
  return digitsA > digitsB ? sa : -sa;
}

/** `str(Decimal)` for a finite or infinite value from `parsePyDecimal`. */
export function decimalString(value) {
  var sign = value.negative ? '-' : '';
  if (value.special === 'inf') {
    return sign + 'Infinity';
  }
  if (value.special !== null) {
    return sign + 'NaN';
  }
  var coeff = value.coeff;
  var leftdigits = value.exp + coeff.length;
  var dotplace = value.exp <= 0 && leftdigits > -6 ? leftdigits : 1;
  var text;
  if (dotplace <= 0) {
    text = '0.' + '0'.repeat(-dotplace) + coeff;
  } else if (dotplace >= coeff.length) {
    text = coeff + '0'.repeat(dotplace - coeff.length);
  } else {
    text = coeff.slice(0, dotplace) + '.' + coeff.slice(dotplace);
  }
  if (leftdigits !== dotplace) {
    var e = leftdigits - dotplace;
    text += 'E' + (e < 0 ? '-' : '+') + Math.abs(e);
  }
  return sign + text;
}
